import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { Container } from 'ui/container'
import { PageTitle } from '../../features/common/page-title'
import { LeaderboardTable } from 'features/leaderboard/table'
import { LeaderboardTimer } from 'features/leaderboard/timer'
import { apiClient } from 'core/http'
import { toast } from 'react-toastify'
import coinIcon from 'features/leaderboard/tabs/assets/images/coin.svg'
import peopleIcon from 'features/leaderboard/tabs/assets/images/people.png'
import c from './assets/sass/page.module.scss'

const PAGE_SIZE = 10

const LeaderboardPage = () => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState('COINS')
    const [entries, setEntries] = useState([])
    const [currentUser, setCurrentUser] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [loading, setLoading] = useState(true)
    const [hasHistory, setHasHistory] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [historyData, setHistoryData] = useState(null)
    const [canLoadMore, setCanLoadMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [availableTypes, setAvailableTypes] = useState(['COINS', 'REFERRALS'])

    const loadCurrent = useCallback(async (type, offset = 0, append = false, limit = PAGE_SIZE) => {
        if (!append) setLoading(true)
        else setLoadingMore(true)
        try {
            const res = await apiClient.get('/leaderboard/current', {
                params: { type, limit, offset }
            })
            if (res?.data?.success) {
                const types = res.data.availableTypes
                if (types) {
                    setAvailableTypes(types)
                    if (!types.includes(type)) {
                        if (types.length > 0) {
                            setActiveTab(types[0])
                        }
                        setLoading(false)
                        setLoadingMore(false)
                        return
                    }
                }
                const newEntries = res.data.entries || []
                setEntries(prev => append ? [...prev, ...newEntries] : newEntries)
                setCurrentUser(res.data.currentUser || null)
                setEndDate(res.data.endDate)
                setHasHistory(res.data.hasHistory || false)
                setCanLoadMore(newEntries.length >= limit && (offset + newEntries.length) < 20)
            }
        } catch (e) {}
        setLoading(false)
        setLoadingMore(false)
    }, [])

    const pollRef = useRef(null)
    const loadedCountRef = useRef(PAGE_SIZE)

    useEffect(() => {
        setEntries([])
        setCanLoadMore(false)
        loadedCountRef.current = PAGE_SIZE
        loadCurrent(activeTab)

        pollRef.current = setInterval(() => {
            loadCurrent(activeTab, 0, false, loadedCountRef.current)
        }, 15000)

        return () => clearInterval(pollRef.current)
    }, [activeTab, loadCurrent])

    const handleLoadMore = () => {
        if (loadingMore) return
        loadCurrent(activeTab, entries.length, true)
        loadedCountRef.current = entries.length + PAGE_SIZE
    }

    const navigate = useNavigate()
    const handleFAQ = () => {
        navigate('/faq?category=leaderboard')
    }

    const handleShowHistory = async () => {
        if (showHistory) {
            setShowHistory(false)
            setHistoryData(null)
            return
        }
        try {
            const res = await apiClient.get('/leaderboard/history', {
                params: { type: activeTab }
            })
            if (res?.data?.success && res.data.leaderboard) {
                setHistoryData(res.data)
                setShowHistory(true)
            }
        } catch (e) {
            toast(t('pages.leaderboard.historyError', 'Ошибка загрузки истории'), { type: 'error' })
        }
    }

    return (
        <div className={c.component}>
            <PageTitle title={t('pages.leaderboard.title')} callback={handleFAQ} />
            <Container>
                <p className={c.subtitle}>{t('pages.leaderboard.subtitle')}</p>

                {!loading && availableTypes.length === 0 ? (
                    <p className={c.empty}>{t('pages.leaderboard.noActive', 'Сейчас нет активных лидербордов')}</p>
                ) : (
                    <>
                        <div className={cn(c.tabs, { [c.singleTab]: availableTypes.length === 1 })}>
                            {availableTypes.includes('COINS') && (
                                <button
                                    className={cn(c.tab, { [c.active]: activeTab === 'COINS' })}
                                    onClick={() => { setActiveTab('COINS'); setShowHistory(false); setHistoryData(null) }}
                                >
                                    <img src={coinIcon} alt="" />
                                    {t('pages.leaderboard.tabCoins')}
                                </button>
                            )}
                            {availableTypes.includes('REFERRALS') && (
                                <button
                                    className={cn(c.tab, { [c.active]: activeTab === 'REFERRALS' })}
                                    onClick={() => { setActiveTab('REFERRALS'); setShowHistory(false); setHistoryData(null) }}
                                >
                                    <img src={peopleIcon} alt="" />
                                    {t('pages.leaderboard.tabReferrals')}
                                </button>
                            )}
                        </div>

                        {endDate && !showHistory && (
                            <LeaderboardTimer endDate={endDate} />
                        )}

                        {hasHistory && (
                            <button className={c.historyBtn} onClick={handleShowHistory}>
                                {t('pages.leaderboard.history')}
                            </button>
                        )}

                        {showHistory && historyData ? (
                            <LeaderboardTable
                                entries={historyData.entries || []}
                                type={activeTab}
                            />
                        ) : (
                            <>
                                <LeaderboardTable
                                    entries={entries}
                                    type={activeTab}
                                    loading={loading}
                                    currentUser={currentUser}
                                />
                                {canLoadMore && (
                                    <button
                                        className={c.loadMore}
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore
                                            ? t('pages.leaderboard.loading', 'Загрузка...')
                                            : t('pages.leaderboard.loadMore')}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}
            </Container>
        </div>
    )
}

export default LeaderboardPage
